import Icon from '../../Icon';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  todayISO,
  maxBookingDateISO,
  formatDisplayDate,
  slotLabel,
  slotStart,
  slotIsAvailable,
  INDIAN_STATES,
  addressTypeLabel,
} from '../../../utils/labTestUtils';

export default function AddressSlotStep({
  currentStep,
  selectedPatient,
  address,
  patientAddresses,
  addressesLoading,
  addressMode,
  selectedSavedAddressId,
  newAddrForm,
  savingPatientAddress,
  addressFormMessage,
  selectSavedAddress,
  switchToSavedAddresses,
  switchToNewAddressForm,
  setNewAddr,
  handleSaveNewAddressToPatient,
  appointmentDate,
  setAppointmentDate,
  slots,
  slotsLoading,
  slotsError,
  selectedSlot,
  setSelectedSlot,
  handleSearchSlots,
}) {
  const navigate = useNavigate();

  return (
    <div className="form-section fade-in-up delay-3">
      <div className="form-section-title">
        <span className={`lab-step-badge${currentStep > 3 ? ' is-done' : ''}`}>3</span>
        Collection address &amp; time slot
      </div>
      <div className="form-section-subtitle">
        Where should the phlebotomist visit, and when should the sample be collected?
      </div>
      <div className="lab-hint-banner">
        <Icon name="help" size={14} />
        <span>
          <strong>Booking window:</strong> Appointments can be scheduled from tomorrow up to 7 days ahead.
          Slots load automatically when you pick a date and have a valid PIN code.
        </span>
      </div>

      {addressesLoading ? (
        <div className="loading-state" style={{ padding: '28px 0' }}>
          <div className="spinner" />
          Loading patient addresses…
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
            marginBottom: 16, fontSize: 13, color: 'var(--text-muted)',
          }}>
            <span>Prefilled from the patient profile (same data as Add Address).</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/patients/${selectedPatient.id}/addresses`)}
            >
              Open Address Management →
            </button>
          </div>

          {patientAddresses.length > 0 && (
            <div className="lab-segment" role="tablist" aria-label="Address source">
              <button
                type="button"
                className={addressMode === 'saved' ? 'is-active' : ''}
                onClick={switchToSavedAddresses}
              >
                Saved addresses
              </button>
              <button
                type="button"
                className={addressMode === 'new' ? 'is-active' : ''}
                onClick={switchToNewAddressForm}
              >
                New address
              </button>
            </div>
          )}

          {addressMode === 'saved' && patientAddresses.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 14,
              marginBottom: 22,
            }}>
              {patientAddresses.map((addr) => (
                <div
                  key={addr.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectSavedAddress(addr)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectSavedAddress(addr); }}
                  className={`address-card${addr.isDefault ? ' primary' : ''}${selectedSavedAddressId === addr.id ? ' is-selected' : ''}`}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  {/* DEF-039: the "Default" badge and the selected-state
                      checkmark (.address-card.is-selected::after, top:12 right:12)
                      were both absolutely positioned into the same corner, so
                      selecting a default address drew the 24px checkmark on top of
                      the badge and visibly clipped it to "DEFAU". The class lets
                      the stylesheet shift the badge clear of the checkmark when
                      both are present — see LabTestFormPage.css. */}
                  {addr.isDefault && (
                    <div className="address-card-default-badge">
                      <span className="badge badge-confirmed">Default</span>
                    </div>
                  )}
                  {/* DEF-039: was `{addr.addressType || 'HOME'}` — a raw enum
                      token, upper-cased with letter-spacing, in a card whose
                      top-right corner is already occupied by the absolutely
                      positioned "Default" badge above. Long values had nowhere
                      to go and clipped. Now a readable label, and the chip
                      reserves room for the badge instead of running under it. */}
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
                    letterSpacing: '0.6px', marginBottom: 10,
                    // Reserve room for whatever occupies the top-right corner:
                    // the Default badge, the selected checkmark, or both.
                    paddingRight:
                      (addr.isDefault ? 76 : 0) +
                      (selectedSavedAddressId === addr.id ? 36 : 0),
                    whiteSpace: 'nowrap',
                  }}>
                    {addressTypeLabel(addr.addressType)}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.75, color: 'var(--text-primary)' }}>
                    <strong>{addr.street || addr.addressLine1}</strong>
                    {(addr.area || addr.addressLine2) && (
                      <><br />{addr.area || addr.addressLine2}</>
                    )}
                    <br />
                    {addr.city}, {addr.state} – {addr.zipCode}
                    <br />
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{addr.country}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(addressMode === 'new' || patientAddresses.length === 0) && (
            <div style={{ marginBottom: 22 }}>
              {patientAddresses.length === 0 && (
                <div className="empty-state" style={{ padding: '16px 14px', marginBottom: 14, textAlign: 'left' }}>
                  <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="pin" size={20} /></div>
                  <div className="empty-title" style={{ fontSize: 14 }}>No addresses on file</div>
                  <div className="empty-desc" style={{ fontSize: 13 }}>
                    Enter the collection address below. You can save it to the patient profile with the button at the bottom.
                  </div>
                </div>
              )}
              <div className="form-section-title" style={{ marginBottom: 14, fontSize: 12 }}>
                {patientAddresses.length === 0 ? 'Collection address' : 'New address for this order'}
              </div>
              <div className="form-grid three-col">
                <div className="form-group">
                  <label className="form-label">Address Type</label>
                  <select className="form-select" value={newAddrForm.addressType} onChange={setNewAddr('addressType')}>
                    <option value="HOME">Home</option>
                    <option value="OFFICE">Office</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '2 / 4' }}>
                  <label className="form-label">Street / Building <span className="required">*</span></label>
                  <input
                    className="form-input"
                    value={newAddrForm.street}
                    onChange={setNewAddr('street')}
                    placeholder="Flat no., Building, Street"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">City <span className="required">*</span></label>
                  <input
                    className="form-input"
                    value={newAddrForm.city}
                    onChange={setNewAddr('city')}
                    placeholder="City"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <select className="form-select" value={newAddrForm.state} onChange={setNewAddr('state')}>
                    {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">PIN Code <span className="required">*</span></label>
                  <input
                    className="form-input"
                    value={newAddrForm.zipCode}
                    onChange={setNewAddr('zipCode')}
                    placeholder="6-digit PIN"
                    inputMode="numeric"
                    maxLength={6}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country <span className="required">*</span></label>
                  <input
                    className="form-input"
                    value={newAddrForm.country}
                    onChange={setNewAddr('country')}
                    placeholder="Country"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Landmark</label>
                  <input
                    className="form-input"
                    value={newAddrForm.landmark}
                    onChange={setNewAddr('landmark')}
                    placeholder="Nearby landmark (optional)"
                  />
                </div>
                <div className="form-group form-full" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="lab-addr-default"
                    checked={newAddrForm.isDefault}
                    onChange={setNewAddr('isDefault')}
                  />
                  <label htmlFor="lab-addr-default" style={{ fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                    Set as default address when saving to profile
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={savingPatientAddress}
                  onClick={handleSaveNewAddressToPatient}
                >
                  {savingPatientAddress ? 'Saving…' : '✓ Save address to patient profile'}
                </button>
              </div>
            </div>
          )}

          {addressFormMessage && (
            <div
              className={
                addressFormMessage.toLowerCase().includes('fail') ||
                addressFormMessage.toLowerCase().includes('required')
                  ? 'alert alert-error'
                  : 'alert alert-success'
              }
              style={{ marginBottom: 14 }}
            >
              {addressFormMessage}
            </div>
          )}

          <div className="form-section-title" style={{ marginTop: 8, marginBottom: 10, borderBottom: 'none', paddingBottom: 0 }}>
            Pick date &amp; time
          </div>
          <div className="lab-date-row">
            <div className="form-group">
              <label className="form-label">Appointment date <span className="required">*</span></label>
              <input
                className="form-input"
                type="date"
                value={appointmentDate}
                min={todayISO()}
                max={maxBookingDateISO()}
                onChange={(e) => { setAppointmentDate(e.target.value); setSelectedSlot(null); }}
                required
              />
              <p className="lab-field-hint">Today through {formatDisplayDate(maxBookingDateISO())}</p>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={handleSearchSlots} disabled={slotsLoading}>
                {slotsLoading ? 'Searching…' : 'Refresh slots'}
              </button>
            </div>
          </div>
        </>
      )}

      {slotsError && <div className="alert alert-error" style={{ marginTop: 14 }}><Icon name="alert" size={14} /> {slotsError}</div>}

      <div className="lab-slots-panel">
        <div className="form-label" style={{ marginBottom: 10 }}>
          Available time slots
          {slotsLoading && <span style={{ fontWeight: 400, marginLeft: 8, color: 'var(--text-muted)' }}>· loading…</span>}
        </div>
        {slots.length === 0 && !slotsLoading ? (
          <p className="lab-field-hint" style={{ marginTop: 0 }}>
            {slotsError
              ? 'Fix the issue above, then refresh slots.'
              : 'Choose a date with a valid PIN code, then tap Refresh slots.'}
          </p>
        ) : (
          <div className="lab-slot-grid">
            {slots.map((s, i) => {
              const avail = slotIsAvailable(s);
              const isSel = selectedSlot &&
                slotStart(selectedSlot) === slotStart(s) &&
                (selectedSlot.endTime || '') === (s.endTime || '');
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!avail}
                  onClick={() => avail && setSelectedSlot(s)}
                  className={`lab-slot-btn${isSel ? ' is-selected' : ''}`}
                  title={avail ? 'Select this slot' : 'Not available'}
                >
                  <span>{slotLabel(s) || '—'}</span>
                  {isSel && <span className="lab-slot-check">Selected</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
